<template>
  <div>
    <div class="text-center">
      <div
        class="btn btn-primary mt-30"
        :disabled="setItUpButtonDisabled"
        @click="onButtonClick"
      >
        Set it up
      </div>
    </div>
    <AppDialog
      id="timesheets-setup-modal"
      title="Set up timesheet approvals"
      :show="showDialog"
      append-to-body="append-to-body"
      @close="close"
    >
      <ValidationObserver ref="dialogObserver">
        <div>
          <p>
            When timesheet approvals are set up, members are required to submit their timesheets for approval before
            payroll is processed. This can be modified in the
            <a :href="timesheetApprovalSettingsUrl">Timesheet approval settings.</a>
          </p>
        </div>
        <ValidationProvider
          v-slot="{ errors }"
          mode="lazy"
          rules="required"
          name="member"
          :model-value="selectedMembers"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Members</label>
            <i
              class="hi hi-info-filled hi-12 a-tooltip members-tooltip"
              data-placement="top auto"
              title=""
              data-original-title="Timesheet approvals will be required for these members"
            ></i>
            <AppLazySelect
              ref="user-selector"
              v-model="selectedMembers"
              eager
              multiple
              resource="member"
              :fetcher="fetcher"
              :organization-id="organizationId"
            >
              <template #item-leading="{ item }">
                <AppAvatar
                  v-if="item.avatar"
                  :avatar="item.avatar"
                  size="speck"
                ></AppAvatar>
              </template>
            </AppLazySelect>
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
        <div>
          <PayPeriodCustomDatesSelector
            :value="customPayPeriodState"
            :initial-state="customPayPeriodSelectorInitialState"
            :default-state="customPayPeriodSelectorDefaultState"
            :pay-period-options="customPayPeriodOptions"
            :organization-timezone="organizationTimezone"
            @input="onCustomPayPeriodSelectorInput"
          />
        </div>
        <div
          v-show="numberOfMembersWithDifferentPayPeriod > 0"
          class="row pay-period-warning"
        >
          <div class="col-sm-12">
            <div class="form-group">
              <div class="dialog-warning orange">
                <i class="hi hi-warning hi-12 mr-10"></i>
                <div>
                  <span class="number-of-members-with-different-period">{{ membersWithDifferentPayPeriodNumber }}</span>
                  <span class="members-with-different-period-text">{{ membersWithDifferentPayPeriodText }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <div class="form-group">
              <ValidationProvider :model-value="payload.enable_timesheets_for_new_members">
                <AppCheckbox v-model="payload.enable_timesheets_for_new_members">
                  Automatically set up new members with timesheet approvals and this pay period
                </AppCheckbox>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <div class="pull-right pt-25">
              <AppButton @click="close">
                {{ configured ? 'Skip' : 'Cancel' }}
              </AppButton>
              <AppButton
                :disabled="disabledSaveButton"
                type="primary"
                class="ml-10"
                @click="submit"
              >
                Save
              </AppButton>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </AppDialog>
  </div>
</template>

<script>
import {
  DEFAULT_PERIOD_SECOND_START_DAY,
  DEFAULT_PERIOD_START_DAY,
  DEFAULT_PERIOD_WEEK_DAY,
  WEEKLY_PAY_PERIOD,
} from '~/app_constants';
import PayPeriodCustomDatesSelector from '~/components/shared/PayPeriodCustomDatesSelector.vue';
import { TimesheetsResource, UserOrganizationsResource } from '~/resources';
import { validatePayPeriodState } from '~/utils/validate_pay_period_state';

export default {
  name: 'TimesheetApprovalSetupDialog',
  components: {
    PayPeriodCustomDatesSelector,
  },
  props: {
    timesheetApprovalSettingsUrl: {
      type: String,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    payPeriods: {
      type: Array,
      required: true,
    },
    configured: {
      type: Boolean,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    organizationTimezone: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    setItUpButtonDisabled: false,
    showDialog: false,
    select2Options: Object.freeze({
      allowClear: false,
      multiple: false,
      width: '100%',
    }),
    payload: {
      ids: [],
      user_organization: {
        timesheets_enabled: true,
        pay_period: '0',
      },
      welcome_mat: true,
      enable_timesheets_for_new_members: true,
    },
    numberOfMembersWithDifferentPayPeriod: 0,
    disabledSaveButton: false,
    selectedMembers: [],
    customPayPeriodState: {
      payPeriodId: WEEKLY_PAY_PERIOD,
      previousPayPeriodId: WEEKLY_PAY_PERIOD,
      periodWeekDay: DEFAULT_PERIOD_WEEK_DAY,
      periodStartDay: null,
      periodSecondStartDay: null,
      periodAnchorDate: null,
    },
  }),
  computed: {
    membersWithDifferentPayPeriodNumber() {
      return this.numberOfMembersWithDifferentPayPeriod === 1
        ? '1 member'
        : this.numberOfMembersWithDifferentPayPeriod + ' members';
    },
    membersWithDifferentPayPeriodText() {
      const verb = this.numberOfMembersWithDifferentPayPeriod === 1 ? 'has' : 'have';

      return ` already ${verb} a pay period set up. By saving this dialog, their pay period will be updated.`;
    },
    customPayPeriodOptions() {
      return this.payPeriods.map((option) => ({ id: option.id, text: option.text }));
    },
    customPayPeriodSelectorInitialState() {
      return {
        initial_pay_period: WEEKLY_PAY_PERIOD,
        initial_period_week_day: DEFAULT_PERIOD_WEEK_DAY,
        initial_period_start_day: null,
        initial_period_second_start_day: null,
        initial_period_anchor_date: null,
        initial_twice_per_month_has_selection: false,
      };
    },
    customPayPeriodSelectorDefaultState() {
      return {
        period_week_day: DEFAULT_PERIOD_WEEK_DAY,
        period_start_day: DEFAULT_PERIOD_START_DAY,
        period_second_start_day: DEFAULT_PERIOD_SECOND_START_DAY,
      };
    },
    effectivePayPeriod() {
      return this.customPayPeriodState.payPeriodId ?? WEEKLY_PAY_PERIOD;
    },
  },
  watch: {
    selectedMembers(value) {
      this.payload.ids = value;
      this.updateNumberOfMembersWithDifferentPayPeriod();
    },
  },
  created() {
    this.payload.user_organization.pay_period = WEEKLY_PAY_PERIOD;
  },
  methods: {
    onButtonClick() {
      this.showDialog = true;
    },
    close() {
      this.showDialog = false;
    },
    fetcher(payload) {
      return TimesheetsResource.members_dropdown({ organization_id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    updateNumberOfMembersWithDifferentPayPeriod() {
      this.numberOfMembersWithDifferentPayPeriod = this.members.filter(
        (member) =>
          this.payload.ids.includes(member[0]) &&
          member[1] !== null &&
          member[1].toString() !== this.effectivePayPeriod,
      ).length;
    },
    onCustomPayPeriodSelectorInput(nextState) {
      this.customPayPeriodState = nextState;
      this.payload.user_organization.pay_period = nextState.payPeriodId;
      this.updateNumberOfMembersWithDifferentPayPeriod();
    },
    validateCustomPayPeriodSelection() {
      const message = validatePayPeriodState(this.customPayPeriodState);

      if (!message) {
        return true;
      }

      this.$appMessage(message);

      return false;
    },
    submit() {
      if (!this.validateCustomPayPeriodSelection()) {
        return;
      }

      this.disabledSaveButton = true;
      const submitPayload = {
        ...this.payload,
        user_organization: {
          ...this.payload.user_organization,
          ...{
            pay_period: this.customPayPeriodState.payPeriodId,
            period_week_day: this.customPayPeriodState.periodWeekDay,
            period_start_day: this.customPayPeriodState.periodStartDay,
            period_second_start_day: this.customPayPeriodState.periodSecondStartDay,
            period_anchor_date: this.customPayPeriodState.periodAnchorDate,
          },
        },
      };

      UserOrganizationsResource.updatePayPeriodsMultiple({ id: this.organizationId }, submitPayload)
        .then((response) => {
          if (response.action === 'reload') {
            this.showDialog = false;
            this.setItUpButtonDisabled = true;
            window.location.reload();
          } else {
            this.$appMessage('Failed to set up timesheets for members');
          }
        })
        .finally(() => {
          this.disabledSaveButton = false;
        });
    },
  },
};
</script>
